import { useRef, useState } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';

import { useNavigate } from 'react-router-dom';
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';

import { crearPersonaSchema } from '../../validation/crearPersonaFormValidation';
import { toTitleCase, toLowerSafe, toUpperSafe } from '../../utils/textFormatter';

import { PARENTESCOS, SEXO, CARGO, PERFIL, ESTADOS_CIVILES } from '../../utils/catalogos/cat-info-personal';
import { COMUNAS_CHILE, REGIONES_CHILE } from '../../utils/catalogos/cat-geografico';

import logo from "../../assets/logos/TRANSKYRIOS-_IconColor.avif";
import ContactoTI from "../../components/contacto-ti/contacto-ti";
import ModuleLayout from '../../layouts/layout-modulo.js';
import './gestion-personas.css'

import { registrarPersona } from '../../services/personasService';
import { usePermisos } from '../../hooks/rolesPermisos';
import { useEmpresas } from '../../hooks/empresasCC';

function CrearPersona() {
  const toast = useRef(null);
  const btnVolverRef = useRef(null);
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { register, handleSubmit, formState: { errors }, 
          control, reset, watch
  } = useForm({
    shouldUnregister: true,
    resolver: yupResolver(crearPersonaSchema),
    defaultValues: {
      region: 2, comuna: 'Antofagasta', crear_usuario: false
    },
    mode: 'onTouched', reValidateMode: 'onChange',
  });

  const tipoPersona = watch('perfil');
  const region = watch("region");
  const crearUsuario = watch("crear_usuario");
  const rolSeleccionado = watch("usr_rol");
  const empresaSeleccionada = watch("empresa");
  const { roles, permisos } = usePermisos(rolSeleccionado);
  const { empresas, centros } = useEmpresas(empresaSeleccionada);

  const limpiarForm = () => reset();

  // Estructurar datos para el envio del formulario
  const estructurarDatos = (data) => {
    return {
      persona: {
        "rut": data.rut || '',
        "nombre": toTitleCase(data.nombre) || '',
        "segundo_nombre": toTitleCase(data.segundo_nombre) || '',
        "apellido_paterno": toTitleCase(data.apellido_paterno) || '',
        "apellido_materno": toTitleCase(data.apellido_materno) || '',
        "email": toLowerSafe(data.correo) || '',
        "celular": data.celular || '',
        "direccion": (toTitleCase(data?.direccion) + " " + data?.direccion_numero) || '',
        "region": data?.region || '',
        "comuna": toTitleCase(data.comuna) || '',
        "tipo_persona": toLowerSafe(data.perfil) || '',
        "crear_usuario": data.crear_usuario || false
      },
      datos_adicionales: {
        "empresa": data.empresa,
        "centro_costo": data.centro_costo,
        "cargo": toUpperSafe(data.cargo) || '',
        "fecha_ingreso": data.fecha_ingreso?.toISOString().split("T")[0] || '',
        "fecha_nacimiento": data.fecha_nacimiento?.toISOString().split("T")[0] || '',
        "sexo": toUpperSafe(data.sexo) || '',
        "estado_civil": toTitleCase(data.estado_civil) || '',
      },
      contacto_emergencia: {
        nombre: toTitleCase(data.contacto_nombre) || '',
        parentesco: toTitleCase(data.contacto_parentesco) || '',
        correo: toLowerSafe(data.contacto_correo) || '',
        celular: data.contacto_celular || ''
      },
      usuario: {
        "crear_usuario": data.crear_usuario || false,
        "usr_rol": data.usr_rol
      }
    }
  }

  const guardarRegistro = async (data) => {
    setIsSubmitting(false);

    try {
      const datos = estructurarDatos(data);
      
      const resp = await registrarPersona(datos);
      if (resp?.ok === false) { throw new Error('No se pudo registrar a la persona'); }
    
      toast.current.show({ 
        severity: 'success', 
        summary: 'Exito', 
        detail: 'Persona registrada correctamente.'
      });

      limpiarForm();
    }
    
    catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error || 'Ocurrió un error al registrar a la persona. Intente nuevamente.',
        life: 3500
      });
    }
    
    finally {
      btnVolverRef.current?.focus?.(); 
      setIsSubmitting(false);
    }
  }

  const seccionEmpresa = (
    <>
      <hr style={{ marginTop: 10, color:'#8a8d91'}}></hr>
      <label className='titulo-seccion-form'>Empresa</label>
      <div className="mb-3 form-comprobante-row">
        {/* Empresa */}
        <div className="form-comprobante-field">
          <label className="form-label">Empresa</label><span className="required">*</span>
            <select {...register('empresa')} className={`form-select ${errors.empresa ? 'is-invalid' : ''}`}>
              <option value="">Seleccione empresa</option>
              {empresas.map((e) => (
                <option key={e.id_empresa} value={e.id_empresa}>
                  {e.nombre}
                </option> 
              ))}
            </select>
          {errors.empresa && <div className="invalid-feedback">{errors.empresa.message}</div>}
        </div>
        {/*  Centro de Costo*/}
        <div className="form-comprobante-field">
          <label className="form-label">Centro de Costo</label><span className="required">*</span>
            <select 
              {...register('centro_costo')} 
              className={`form-select ${errors.centro_costo ? 'is-invalid' : ''}`}
              disabled={!empresaSeleccionada}
            >
              <option value="">
                {empresaSeleccionada
                ? "Seleccione centro de costo"
                : "Primero seleccione una empresa"}
              </option>

              {centros.map((cc) => (
                <option key={cc.id_centrocosto} value={cc.id_centrocosto}>
                  {cc.codigo} {cc.nombre?.trim() ? `- ${cc.nombre}` : ''}
                </option>
              ))}
            </select>
          {errors.centro_costo && <div className="invalid-feedback">{errors.centro_costo.message}</div>}
        </div>
      </div>
    </>
  );

  return (
    <ModuleLayout titulo="Registrar Persona" logo={logo} onVolver={() => navigate('/app')}>
      <Toast ref={toast} position="center"/>
      <section className="tmc-content mb-4">
        <h3 className='titulo-form'>➕ Crear Persona</h3>
        <section className="form-section">
          <form className="container-form">
            {/* Información personal */}
            <div className='card-nc'>
              <label className='titulo-seccion-form'>Información personal</label>

              <div className="mb-3 form-comprobante-row">
                {/* RUT */}  
                <div className="form-comprobante-field">
                  <label className="form-label">RUT</label><span className="required">*</span>
                  <input
                    type="text"
                    placeholder='Ej. 12345678-9'
                    className={`form-control ${errors.rut ? 'is-invalid' : ''}`}
                    {...register("rut")}
                  />
                  {errors.rut && <div className="invalid-feedback">{errors.rut.message}</div>}
                </div>
              </div> 

              <div className="mb-3 form-comprobante-row">
                {/* Nombre */}
                <div className="form-comprobante-field">
                  <label className="form-label">Nombre</label><span className="required">*</span>
                  <input
                    type="text"
                    placeholder='Ej. Pedro'
                    className={`form-control ${errors.nombre ? 'is-invalid' : ''}`}
                    {...register("nombre")}
                  />
                  {errors.nombre && <div className="invalid-feedback">{errors.nombre.message}</div>}
                </div>
                {/* Segundo Nombre */}
                <div className="form-comprobante-field">
                  <label className="form-label">Segundo Nombre</label>
                  <input 
                    type="text" 
                    placeholder='Ej. Andrés'
                    className='form-control' 
                    {...register("segundo_nombre")} 
                  />
                </div>
              </div>

              <div className="mb-3 form-comprobante-row">
                {/* Apellido Paterno */}
                <div className="form-comprobante-field">
                  <label className="form-label">Apellido Paterno</label><span className="required">*</span>
                  <input 
                    type="text" 
                    placeholder='Ej. Morales'
                    className={`form-control ${errors.apellido_paterno ? 'is-invalid' : ''}`} 
                    {...register("apellido_paterno")}
                  />
                  {errors.apellido_paterno && <div className="invalid-feedback">{errors.apellido_paterno.message}</div>}
                </div>
                {/* Apellido Materno */}
                <div className="form-comprobante-field">
                  <label className="form-label">Apellido Materno</label><span className="required">*</span>
                  <input 
                    type="text" 
                    placeholder='Ej. Castillo'
                    className={`form-control ${errors.apellido_materno ? 'is-invalid' : ''}`} 
                    {...register("apellido_materno")} 
                  />
                  {errors.apellido_materno && <div className="invalid-feedback">{errors.apellido_materno.message}</div>}
                </div>
              </div>

              <div className="mb-3 form-comprobante-row">
                {/* Celular */}
                <div className="form-comprobante-field">
                  <label className="form-label">Celular (+56)<span className="required">*</span></label>
                  <input 
                    type="number"
                    placeholder="9 0000 0000"
                    className={`sin-flechas form-control ${errors.celular ? 'is-invalid' : ''}`}
                    {...register('celular')}
                  />
                  {errors.celular && <div className="invalid-feedback">{errors.celular.message}</div>}
                </div>
                {/* Correo */}
                <div className="form-comprobante-field">
                  <label className="form-label">Correo electrónico<span className="required">*</span></label>
                  <input 
                    type="email"
                    placeholder="ejemplo@ejemplo.com"
                    className={`form-control ${errors.correo ? 'is-invalid' : ''}`}
                    {...register('correo')}
                  /> {errors.correo && <div className="invalid-feedback">{errors.correo.message}</div>}
                </div>
              </div>

              <div className="mb-3 form-comprobante-row">
                {/* Region */}
                <div className="form-comprobante-field">
                  <label className="form-label">Región</label><span className="required">*</span>
                    <select {...register('region')}  className={`form-select ${errors.region ? 'is-invalid' : ''}`} >
                      <option value="">Seleccione región</option>
                      {REGIONES_CHILE.map( r => (
                        <option key={r.nombre} value={r.id}>
                          {r.nombre}
                        </option> 
                      ))}
                    </select>
                  {errors.region && <div className="invalid-feedback">{errors.region.message}</div>}
                </div>
                {/* Comuna */}
                <div className="form-comprobante-field">
                  <label className="form-label">Comuna</label><span className="required">*</span>
                    <select 
                      {...register('comuna')} 
                      className={`form-select ${errors.comuna ? 'is-invalid' : ''}`}
                      disabled={!region}
                    >
                      <option value="">Seleccione comuna</option>
                      {COMUNAS_CHILE[region]?.map( c => (
                        <option key={c} value={c}>
                          {c}
                        </option> 
                      ))}
                    </select>
                  {errors.comuna && <div className="invalid-feedback">{errors.comuna.message}</div>}
                </div>
              </div>

              <div className="mb-3 form-comprobante-row">
                {/* Perfil */}
                <div className="form-comprobante-field">
                  <label className="form-label">Perfil</label><span className="required">*</span>
                  <select {...register('perfil')} className={`form-select ${errors.perfil ? 'is-invalid' : ''}`}>
                    <option value="">Seleccione perfil</option>
                    {PERFIL.map((p) => (
                      <option key={p.value} value={p.value}>
                        {p.label}
                      </option> 
                    ))}
                  </select>
                  {errors.perfil && <div className="invalid-feedback">{errors.perfil.message}</div>}
                </div>
              </div>
            </div>

            { tipoPersona && ( 
              <div className='card-nc'>
                <label className='titulo-seccion-form'>Información adicional</label>
                
                {/* Perfil Cliente*/}
                { tipoPersona === 'cliente' && (
                  <>
                    {seccionEmpresa}
                  </>
                )}

                {/* Perfil Trabajador*/}
                { tipoPersona === 'trabajador' && (
                  <>
                    <hr style={{ marginTop: 10, color:'#8a8d91'}}></hr>
                    <label className='titulo-seccion-form'>Personal</label>

                    <div className="mb-3 form-comprobante-row">
                      {/* Estado Civil */}
                      <div className="form-comprobante-field">
                        <label className="form-label">Estado Civil</label><span className="required">*</span>
                          <select {...register("estado_civil")} className={`form-select ${errors.estado_civil ? 'is-invalid' : ''}`}>
                            <option value="">Seleccione estado civil</option>
                            {ESTADOS_CIVILES.map(e => (
                              <option key={e.value} value={e.value}>
                                {e.label}
                              </option>
                            ))}
                          </select>
                        {errors.estado_civil && <div className="invalid-feedback">{errors.estado_civil.message}</div>}
                      </div>
                    </div>

                    <div className="mb-3 form-comprobante-row">
                      {/* Dirección */}
                      <div className="form-comprobante-field">
                        <label className="form-label">Dirección</label><span className="required">*</span>
                        <input 
                          type="text" 
                          placeholder='Ej. Av. Antonio Rendic 3758'
                          className={`form-control ${errors.direccion ? 'is-invalid' : ''}`} 
                          {...register("direccion")} 
                        />
                        {errors.direccion && <div className="invalid-feedback">{errors.direccion.message}</div>}
                      </div>
                      {/* Dirección Número */}
                      <div className="form-comprobante-field">
                        <label className="form-label">Casa/Depto</label><span className="required">*</span>
                        <input 
                          placeholder='Ej. Casa 4 / Depto 203 / Sin número'
                          className={`form-control ${errors.direccion_numero ? 'is-invalid' : ''}`} 
                          {...register("direccion_numero")} 
                        />
                        {errors.direccion_numero && <div className="invalid-feedback">{errors.direccion_numero.message}</div>}
                      </div>
                    </div>
                    
                    <div className="mb-3 form-comprobante-row">
                      {/* Fecha Nacimiento */}
                      <div className="form-comprobante-field">
                        <label className="form-label">Fecha de Nacimiento<span className="required">*</span></label>
                        <input type='date' 
                          className={`form-control ${errors.fecha_nacimiento ? 'is-invalid' : ''}`} 
                          {...register('fecha_nacimiento')}/>
                        {errors.fecha_nacimiento && ( <div className="invalid-feedback">{errors.fecha_nacimiento.message}</div>)}
                      </div>
                      {/* Sexo */}
                      <div className="form-comprobante-field">
                        <label className="form-label">Sexo</label><span className="required">*</span>
                          <select {...register('sexo')} className={`form-select ${errors.sexo ? 'is-invalid' : ''}`}>
                            <option value="">Seleccione sexo</option>
                            {SEXO.map((s) => (
                              <option key={s.value} value={s.value}>
                                {s.label}
                              </option> 
                            ))}
                          </select>
                        {errors.sexo && <div className="invalid-feedback">{errors.sexo.message}</div>}
                      </div>
                    </div>

                    {seccionEmpresa}
                    <div className="mb-3 form-comprobante-row">
                      {/* Fecha Ingreso */}
                      <div className="form-comprobante-field">
                        <label className="form-label">Fecha de Ingreso<span className="required">*</span></label>
                        <input type='date' 
                          className={`form-control ${errors.fecha_ingreso ? 'is-invalid' : ''}`} 
                          {...register('fecha_ingreso')}/>
                        {errors.fecha_ingreso && ( <div className="invalid-feedback">{errors.fecha_ingreso.message}</div>)}
                      </div>
                      {/* Cargo */}
                      <div className="form-comprobante-field">
                        <label className="form-label">Cargo</label><span className="required">*</span>
                          <select {...register('cargo')} className={`form-select ${errors.cargo ? 'is-invalid' : ''}`}>
                            <option value="">Seleccione cargo</option>
                            {CARGO.map((c) => (
                              <option key={c.value} value={c.value}>
                                {c.label}
                              </option> 
                            ))}
                          </select>
                        {errors.cargo && <div className="invalid-feedback">{errors.cargo.message}</div>}
                      </div>
                    </div>

                    <hr style={{ marginTop: 35, color:'#8a8d91'}}></hr>
                    <label className='titulo-seccion-form'>Contacto de emergencia</label>
                    
                    <div className="mb-3 form-comprobante-row">
                      {/* Contacto Nombre Completo */}
                      <div className="form-comprobante-field">
                        <label className="form-label">Nombre y Apellidos</label><span className="required">*</span>
                        <input 
                          type="text" 
                          placeholder='Ej. Juana Medina Rojas'
                          className={`form-control ${errors.contacto_nombre ? 'is-invalid' : ''}`} 
                          {...register("contacto_nombre")} 
                        />
                        {errors.contacto_nombre && <div className="invalid-feedback">{errors.contacto_nombre.message}</div>}
                      </div>
                      {/* Contacto Parentesco */}
                      <div className="form-comprobante-field">
                        <label className="form-label">Parentesco</label><span className="required">*</span>
                        <select {...register('contacto_parentesco')} className={`form-select ${errors.contacto_parentesco ? 'is-invalid' : ''}`}>
                          <option value="">Seleccione parentesco</option>
                          {PARENTESCOS.map((p) => (
                            <option key={p.value} value={p.value}>
                              {p.label}
                            </option> 
                          ))}
                        </select>
                        {errors.contacto_parentesco && <div className="invalid-feedback">{errors.contacto_parentesco.message}</div>}
                      </div>
                    </div>

                    <div className="mb-3 form-comprobante-row">
                      {/* Contacto Celular */}
                      <div className="form-comprobante-field">
                        <label className="form-label">Celular (+56)<span className="required">*</span></label>
                        <input 
                          type="number"
                          placeholder="9 0000 0000"
                          className={`sin-flechas form-control ${errors.contacto_celular ? 'is-invalid' : ''}`}
                          {...register('contacto_celular')}
                        />
                        {errors.contacto_celular && <div className="invalid-feedback">{errors.contacto_celular.message}</div>}
                      </div>
                      {/* Contacto Correo */}
                      <div className="form-comprobante-field">
                        <label className="form-label">Correo electrónico<span className="required">*</span></label>
                        <input 
                          type="email"
                          placeholder="ejemplo@ejemplo.com"
                          className={`form-control ${errors.contacto_correo ? 'is-invalid' : ''}`}
                          {...register('contacto_correo')}
                        /> {errors.contacto_correo && <div className="invalid-feedback">{errors.contacto_correo.message}</div>}
                      </div>
                    </div>
                  </>
                )}
              </div>
            )}

            {/* Crear usuario */}
            <div className='card-nc'>
              <label className='titulo-seccion-form'>
                ¿Desea generar credenciales de acceso para esta persona?
              </label>

              <p className="text-muted" style={{ textAlign: 'justify' }}>
                Al marcar la casilla, se crearán automáticamente las credenciales de acceso 
                y se enviarán al correo electrónico de la persona que indicó en el formulario.
              </p>

              <div className="mb-3 form-comprobante-row justify-content-center">
                <Controller
                  name="crear_usuario"
                  control={control}
                  defaultValue={false}
                  render={({ field }) => (
                    <div className="form-comprobante-row">
                      <Checkbox
                        inputId="crear_usuario"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.checked)}
                      />
                      <label htmlFor="crear_usuario">Registrar usuario</label>
                    </div>
                  )}
                />
              </div>

              { crearUsuario && (
                <div>
                  <div className="mb-3 form-comprobante-field">
                    <label className="form-label">Defina rol del usuario</label><span className="required">*</span>
                      <select {...register("usr_rol")} className= {`form-select ${errors.usr_rol ? 'is-invalid' : ''}`}>
                        <option value="">Roles disponibles</option>
                        {roles.map(r => (
                          <option key={r.id} value={r.id}>
                            {r.nombre}
                          </option>
                        ))}
                      </select>
                    {errors.usr_rol && <div className="invalid-feedback">{errors.usr_rol.message}</div>}
                  </div>

                  {rolSeleccionado ? (
                    <div className="alert alert-primary" role="alert">
                      <label className="form-label font-bold">Permisos asociados: </label>
                      <div className="rounded-3 overflow-hidden">
                        <table className="table table-light table-hover table-striped table-sm mb-1">
                          <thead>
                            <tr>
                              <th scope="col">Permiso</th>
                              <th scope="col">Módulo</th>
                            </tr>
                          </thead>
                          <tbody>
                            {permisos.map((p, index) => (
                              <tr key={index}>
                                <td>{p.funcionalidad}</td>
                                <td>{p.modulo}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ) : (
                    <div className="alert alert-primary ">
                      Selecciona un rol para ver sus permisos asociados.
                    </div>
                  )}
                </div>
              )}
            </div>

            {/* Sección botones */}
            <div className='tmc-btns-container'>
              <Button type="button" label="Guardar" icon="pi pi-save" 
                className="inv-btn-confirm"
                onClick={handleSubmit(guardarRegistro)}
                disabled={isSubmitting}
              />
            </div>
          </form>
        </section>
      </section>
      <section className="per-content">
        <ContactoTI />
      </section>
    </ModuleLayout>
  );
}

export default CrearPersona